import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import {
  getCredits,
} from "../redux/actions/payment";
import instance from "../redux/actions/AxiosInstance/authenticated";
import { FaAmazon, FaRegCopy } from "react-icons/fa";
import { toast } from "sonner";
import { formatHistoryDate } from "../utils/helper";
import { useSearchParams } from "react-router-dom";
import ProductDetail from "./ProductDetail";

export const Dashboard = (props) => {
  const [searchParams] = useSearchParams();
  const asin = searchParams.get('asin');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState({ total: 0, data: [] });
  const [recentHistory, setRecentHistory] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState(null);
  const [amazonConnected, setAmazonConnected] = useState(false);
  const [checkingAmazonStatus, setCheckingAmazonStatus] = useState(true);
  const [amazonLoading, setAmazonLoading] = useState(false);
  const dispatch = useDispatch();

  const fetchWeeklyScans = async () => {
    try {
      const response = await instance.get('/getscans');
      const scanData = response.data || [];
      const processedData = [];
      let total = 0;
      
      const scanMap = new Map();
      scanData.forEach(scan => {
        if (scan.scanDate && scan.scanCount) {
          const dateKey = new Date(scan.scanDate).toDateString();
          scanMap.set(dateKey, (scanMap.get(dateKey) || 0) + scan.scanCount);
        }
      });
      
      const today = new Date();
      const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        date.setHours(0, 0, 0, 0);
        
        const dateKey = date.toDateString();
        const dayName = daysOfWeek[date.getDay()];
        const count = scanMap.get(dateKey) || 0;
        
        total += count;
        processedData.push({ 
          day: dayName, 
          count, 
          date: date.toISOString().split('T')[0]
        });
      }
      
      setWeeklyStats({ total, data: processedData });
    } catch (error) {
      console.error('Error fetching weekly scans:', error);
      setWeeklyStats({ total: 0, data: [] });
    }
  };

  const fetchRecentHistory = async (limit = 5) => {
    try {
      setRecentLoading(true);
      setRecentError(null);
      const response = await instance.post(`/history?limit=${limit}&page=1`);
      const items = (response?.data?.data || []).map((entry) => {
        const productData = entry?.data || {};
        const isWalmart =
          productData?.platform === "walmart" ||
          (!!productData.product_id && !productData.asin);

        const walmartImage =
          productData.main_image ||
          (Array.isArray(productData.images) && productData.images[0]) ||
          null;

        const amazonImage = productData.image || null;

        const walmartPrice =
          (productData.price && productData.price.current != null
            ? productData.price.current
            : null) ?? 0;

        const amazonPrice =
          productData?.overview?.prices?.sale_price != null
            ? productData.overview.prices.sale_price
            : 0;

        const title = isWalmart
          ? productData.title || "Unknown Walmart Product"
          : productData?.basicInfo?.productName || "Unknown Product";

        const idValue = isWalmart
          ? productData.product_id || ""
          : productData.asin || "";

        const idLabel = isWalmart ? "Product ID" : "ASIN";

        return {
          platform: isWalmart ? "walmart" : "amazon",
          asin: productData.asin || "",
          product_id: productData.product_id || "",
          idLabel,
          idValue,
          title,
          price: isWalmart ? walmartPrice : amazonPrice,
          image: isWalmart ? walmartImage : amazonImage,
          createdAt: entry?.createdAt,
        };
      });
      setRecentHistory(items);
    } catch (err) {
      setRecentError('Failed to load recent history');
      setRecentHistory([]);
    } finally {
      setRecentLoading(false);
    }
  };

  const checkAmazonStatus = async () => {
    try {
      setCheckingAmazonStatus(true);
      const response = await instance.get('/amazon-seller/auth-status');
      
      if (response.data.success) {
        setAmazonConnected(response.data.isConnected);
      }
    } catch (error) {
      console.error('Error checking Amazon status:', error);
      setAmazonConnected(false);
    } finally {
      setCheckingAmazonStatus(false);
    }
  };

  const handleAmazonLogin = async () => {
    try {
      setAmazonLoading(true);
      
      const response = await instance.get('/amazon-seller/consent-url');
      
      if (response.data.success && response.data.consentUrl) {
        window.location.href = response.data.consentUrl;
      } else {
        toast.error('Failed to generate Amazon consent URL');
      }
    } catch (error) {
      console.error('Amazon login error:', error);
      toast.error(
        error.response?.data?.message || 
        'Failed to connect to Amazon. Please try again.'
      );
    } finally {
      setAmazonLoading(false);
    }
  };

  useEffect(() => {
    dispatch(getCredits());
    fetchWeeklyScans();
    fetchRecentHistory(4);
    checkAmazonStatus();

    const queryParameters = new URLSearchParams(window.location.search);
    const payment_status = queryParameters.get("payment_status");
    const session_id = queryParameters.get("session_id");
    if (payment_status) {
      if (payment_status === "success") {
        setPaymentSuccess(true);
        setShowPaymentModal(true);
      } else if (payment_status === "failed") {
        setPaymentSuccess(false);
        setShowPaymentModal(true);
      }
      if (session_id) {
        queryParameters.delete("session_id");
      }
      queryParameters.delete("payment_status");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  return (
    <div className="bg-primary-bg min-h-screen">
      <p className="title border-b-2 border-b-border w-full p-5 text-gray-100">
        Dashboard
      </p>
      <div className="px-1 md:px-8 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-5 pt-4 w-full mb-5">
          <div className={`bg-primary-bg rounded-2xl p-6 ${(!checkingAmazonStatus && !amazonConnected) ? 'lg:col-span-3' : 'lg:col-span-5'} border-top-fade`}>
            <h3 className="text-white font-semibold mb-4">Total Searches This Week</h3>
            <div className="flex items-baseline mb-3">
              <span className="text-6xl font-bold text-white">{weeklyStats.total}</span>
            </div>
            
            <div className={`flex items-end justify-between ${(!checkingAmazonStatus && !amazonConnected) ? 'md:gap-3' : 'md:gap-16'} pt-1 pb-2 gap-2`} style={{ height: '280px' }}>
              {weeklyStats.data && weeklyStats.data.length > 0 ? (() => {
                const maxCount = Math.max(...weeklyStats.data.map(d => d.count));
                const hasData = maxCount > 0;
                const chartHeight = 260;
                const minBarHeight = 70;
                
                return weeklyStats.data.map((item, idx) => {
                  let barHeight = minBarHeight;
                  
                  if (hasData && item.count > 0) {
                    if (maxCount === item.count) {
                      barHeight = chartHeight;
                    } else {
                      const ratio = Math.sqrt(item.count) / Math.sqrt(maxCount);
                      barHeight = Math.max(minBarHeight, ratio * chartHeight);
                    }
                  }
                  
                  const baseColor = '#1c211d';
                  const hoverColor = '#4F4F4F';
                  return (
                    <div key={`${item.day}-${idx}`} className="flex-1 flex flex-col items-center group">
                      <div className="w-full flex flex-col items-center mb-2">
                        <div 
                          className="w-full rounded-xl transition-all duration-300 flex items-end justify-center relative border border-[#3F3F3F]"
                          style={{ 
                            height: `${Math.round(barHeight)}px`,
                            backgroundColor: baseColor,
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = hoverColor;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = baseColor;
                          }}
                          title={`${item.day}: ${item.count} scans${item.date ? ` (${item.date})` : ''}`}
                        >
                          <span className="text-2xl font-semibold text-white select-none mb-4">
                            {item.count}
                          </span>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 transition-colors duration-200">
                        {item.day}
                      </span>
                    </div>
                  );
                });
              })() : (
                ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                  <div key={`empty-${day}-${idx}`} className="flex-1 flex flex-col items-center">
                    <div className="w-full flex flex-col items-center mb-2">
                      <div 
                        className="w-full bg-[#2F2F2F] rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-[#3F3F3F]" 
                        style={{ height: '20px' }}
                        title={`${day}: No data available`}
                      >
                        <span className="text-xs font-semibold text-gray-500">0</span>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">{day}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {!checkingAmazonStatus && !amazonConnected && (
            <div className="bg-primary-bg rounded-2xl p-6 border-2 border-orange-500 relative overflow-hidden lg:col-span-2 flex flex-col justify-evenly">
              <FaAmazon className="text-[70px] text-white" />
              <div className="">
                <p className="text-white text-4xl leading-relaxed">
                  Check your amazon eligibility of products by linking your account now
                </p>
              </div>
              <button
                onClick={handleAmazonLogin}
                disabled={amazonLoading}
                className="w-full bg-primary text-primary-dark font-bold py-3 px-4 rounded-lg hover:opacity-90 transition duration-300"
              >
                Link Amazon Account
              </button>
            </div>
          )}
        </div>

        <div className="bg-primary-bg rounded-2xl p-6 border-top-fade">
          <h3 className="text-white text-xl font-semibold mb-4">Recently Viewed</h3>
          <div className="space-y-3">
            {recentLoading && (
              <div className="text-gray-400 text-sm">Loading...</div>
            )}
            {!recentLoading && recentError && (
              <div className="text-red-400 text-sm">{recentError}</div>
            )}
            {!recentLoading && !recentError && recentHistory.length === 0 && (
              <div className="text-gray-400 text-sm">No recent items</div>
            )}
            {!recentLoading && !recentError && recentHistory.length > 0 && (
              recentHistory.map((item, idx) => (
                <div
                  key={`${item.idValue || item.asin || 'id'}-${idx}`}
                  className="bg-secondary-bg rounded-lg p-4 flex items-center gap-4"
                >
                  <div className="relative w-16 h-16 bg-white rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title || item.idValue}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">Img</span>
                    )}
                    {/* Platform badge */}
                    <span
                      className={`absolute -top-[0px] -right-[5px] px-2 py-0.5 rounded-full text-[9px] font-semibold ${
                        item.platform === "walmart"
                          ? "bg-blue-500 text-white"
                          : "bg-yellow-500 text-primary-dark"
                      }`}
                    >
                      {item.platform === "walmart" ? "Walmart" : "Amazon"}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-400 text-xs mb-2">
                      {item.createdAt ? formatHistoryDate(item.createdAt) : ""}
                    </p>
                    <p className="text-white mb-1">
                      {item.title && item.title.trim().length > 50
                        ? item.title.trim().substring(0, 50) + "..."
                        : (item.title || "").trim() || "Unknown Product"}
                    </p>
                    <p className="text-gray-400 text-xs flex items-center">
                      <span>
                        {item.idLabel}: {item.idValue || "-"}
                      </span>
                      <span
                        className="inline-flex items-center ml-2 justify-center cursor-pointer"
                        title={
                          item.idValue
                            ? `Copy ${item.idLabel} ${item.idValue}`
                            : `No ${item.idLabel} to copy`
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          if (item.idValue && navigator?.clipboard?.writeText) {
                            navigator.clipboard.writeText(item.idValue);
                          }
                          toast.success(
                            `${item.idLabel} copied to clipboard`
                          );
                        }}
                        role="button"
                        aria-label={`Copy ${item.idLabel}`}
                      >
                        <FaRegCopy className="w-3 h-3 text-primary" />
                      </span>
                    </p>
                  </div>
                  <div className="flex flex-col items-end">
                    {item.platform !== "walmart" && !amazonConnected && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 text-primary text-xs mb-2 border border-primary rounded-md px-2 py-1 hover:bg-primary hover:text-primary-dark transition duration-300"
                        onClick={handleAmazonLogin}
                        disabled={amazonLoading}
                      >
                        <FaAmazon className="text-base" />
                        <span>Link Account</span>
                      </button>
                    )}
                    {item.price ? (
                      <p className="text-white font-bold text-2xl">{`$${item.price}`}</p>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {showPaymentModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 w-96 text-center animate-fade-in relative">
            {!paymentSuccess && <button
              className="absolute top-2 right-4 text-gray-500 hover:text-gray-700 text-3xl font-bold animate-fade-in "
              onClick={() => setShowPaymentModal(false)}
            >
              &times;
            </button>}
            {paymentSuccess ? (
              <>
                <div className="flex flex-col items-center">
                  <svg className="w-16 h-16 text-green-500 mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2l4-4"/></svg>
                  <h2 className="text-2xl font-bold mb-2 text-green-700">Subscription Purchased!</h2>
                  <p className="mb-6 text-gray-700">Your subscription was successful. Your credits will be added shortly, please try refreshing after a few seconds.</p>
                  <button
                    className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition"
                    onClick={() => window.location.reload()}
                  >
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col items-center">
                  <svg className="w-16 h-16 text-red-500 mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 9l-6 6m0-6l6 6"/></svg>
                  <h2 className="text-2xl font-bold mb-2 text-red-700">Payment Failed</h2>
                  <p className="mb-4 text-gray-700">Unfortunately, your payment could not be processed. Please try again or contact support.</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
